import fetchLocationDetails from '@dehaat/kisan-app-bl/api/fetchLocationDetails';
import getBlocksByDistrict from '@dehaat/kisan-app-bl/api/getBlocksByDistrict';
import getVillagesByBlock from '@dehaat/kisan-app-bl/api/getVillagesByBlock';
import { AddressReqObj } from '@dehaat/kisan-app-bl/models/Address';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import ErrorModal from '@/components/ErrorModal';
import TextField from '@/components/input/TextField2';
import Okay from '@/components/modalActions/Okay';
import { useAppSelector } from '@/hooks/reduxHooks';
import useErrorModalState from '@/hooks/useErrorModalState';
import useForm from '@/hooks/useForm';
import { selectCurrentLocation } from '@/store/features/locationSlice';
import { Address, Location, Option, validator } from '@/utils/address';
import { addNewAddressV3 } from '@/utils/api/addNewAddressV3';
import Spinner from '../Spinner';
import ActionButton from './ActionButton';
interface Props {
  handleSave?: (address?: Address) => void;
  isPopup?: boolean;
}
const AddNewFarmerWithAddress = ({
  handleSave,
  isPopup = false
}: Props) => {
  // The query parameter 'p' is used to disable phone number editing
  // One use case where this is used is when editing address from cart
  const {
    back
  } = useRouter();
  const {
    t
  } = useTranslation('address');
  const currentLocation = useAppSelector(selectCurrentLocation);
  const initialState: Address = {
    address: '',
    block: '',
    fullName: '',
    landmark: '',
    mobileNumber: '',
    pinCode: '',
    village: ''
  };
  const [requestedForLocation, setRequestedForLocation] = useState(false);
  const [blocks, setBlocks] = useState<Option[]>([]);
  const [villages, setVillages] = useState<Option[]>([]);
  const [loading, setLoading] = useState({
    address: false,
    blocks: false,
    location: false,
    updating: false,
    villages: false,
    currentLocation: false
  });
  const {
    error,
    state: formState,
    setState: setFormState,
    handleInputChange,
    handleInputBlur,
    isFormInvalid,
    updateError
  } = useForm<Address>(initialState, validator);
  const {
    state: {
      state = '',
      district = '',
      kheti_state_id,
      kheti_district_id,
      kheti_block_id
    },
    setState: setLocationDetails
  } = useForm<Location>({});
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  useEffect(() => {
    const pinCode = formState.pinCode as string;
    const fetchAndUpdateLocation = async () => {
      setLoading(prev => ({
        ...prev,
        location: true
      }));
      const locationDetails = await fetchLocationDetails(pinCode);
      if (locationDetails) {
        setLocationDetails(locationDetails);
        setFormState(prev => ({
          ...prev,
          block: locationDetails.block
        }));
      } else {
        updateError('pinCode', true);
      }
      setLoading(prev => ({
        ...prev,
        location: false
      }));
      setRequestedForLocation(false);
    };
    if (pinCode.toString().length === 6) {
      fetchAndUpdateLocation();
    } else {
      setLocationDetails({});
      setFormState(prev => ({
        ...prev,
        block: '',
        village: ''
      }));
    }
  }, [formState.pinCode, setFormState, setLocationDetails, updateError]);

  // 'getBlocks' useEffect
  // Loads all the blocks when kheti_district_id changes
  useEffect(() => {
    const fetchAndUpdateBlockList = async (districtId: number) => {
      setLoading(prev => ({
        ...prev,
        blocks: true
      }));
      const data = await getBlocksByDistrict(districtId);
      setBlocks(data.map(b => ({
        id: b.id.toString(),
        name: b.name
      })));
      setLoading(prev => ({
        ...prev,
        blocks: false
      }));
    };
    if (kheti_district_id) {
      fetchAndUpdateBlockList(Number(kheti_district_id));
    } else {
      setBlocks([]);
    }
  }, [kheti_district_id]);

  // 'getVillages' useEffect
  // Loads all the villages when kheti_block_id changes
  useEffect(() => {
    const fetchAndUpdateVillageList = async (blockId: number) => {
      setLoading(prev => ({
        ...prev,
        villages: true
      }));
      const data = await getVillagesByBlock(blockId);
      setVillages(data.map(v => ({
        id: v.id.toString(),
        name: v.name
      })));
      setLoading(prev => ({
        ...prev,
        villages: false
      }));
    };
    if (kheti_block_id) {
      fetchAndUpdateVillageList(Number(kheti_block_id));
    } else {
      setVillages([]);
    }
  }, [kheti_block_id]);
  const {
    address,
    block: formBlock,
    fullName,
    landmark,
    mobileNumber,
    pinCode,
    village: formVillage
  } = formState;

  // Update kheti_block_id in the form state when selected block changes
  useEffect(() => {
    const selectedBlockId = blocks.find(b => b.name === formBlock)?.id;
    if (selectedBlockId) {
      setLocationDetails(prev => ({
        ...prev,
        kheti_block_id: Number(selectedBlockId)
      }));
    }
  }, [blocks, formBlock, setLocationDetails]);
  const handleSaveAndProceed = async () => {
    setLoading(prev => ({
      ...prev,
      updating: true
    }));
    let success = false;
    const selectedVillageId = villages.find(v => v.name === formVillage)?.id;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body: AddressReqObj = {
      pin_code: pinCode as string,
      state_id: kheti_state_id as number,
      state: state,
      district_id: kheti_district_id as number,
      district: district,
      block_id: kheti_block_id as number,
      block: formBlock,
      street_address_1: address,
      street_address_2: landmark,
      contact_name: fullName,
      contact_number: mobileNumber as string
    };
    if (selectedVillageId) {
      body.village_id = Number(selectedVillageId);
      body.village = formVillage;
    }
    const response = await addNewAddressV3(body);
    success = Boolean(response?.id);
    if (success) {
      handleSave ? handleSave(response as unknown as Address) : back();
    } else {
      setLoading(prev => ({
        ...prev,
        updating: false
      }));
    }
  };
  useEffect(() => {
    updatePinCodeFromLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, requestedForLocation]);
  const updatePinCodeFromLocation = () => {
    if (currentLocation && requestedForLocation) {
      setLoading(prev => ({
        ...prev,
        currentLocation: false
      }));
      setFormState(prev => ({
        ...prev,
        pinCode: currentLocation.postalCode,
        address: currentLocation.formattedAddress
      }));
    }
  };
  const finalLoading = loading.address || loading.blocks || loading.location || loading.updating || loading.villages || loading.currentLocation;
  return <div className="relative flex flex-col h-screen" data-sentry-component="AddNewFarmerWithAddress" data-sentry-source-file="AddNewFarmerWithAddress.tsx">
      <div className="pt-14 w-full">
        <div className="flex flex-col gap-6 px-4 py-10">
          <p className="font-semibold text-neutral-90 text-lg">
            {t('create_farmer_profile')}
          </p>
          <TextField testId="add_edit_mobile_number" name="mobileNumber" label={t('farmer_phone_number')} value={mobileNumber} onChange={handleInputChange} onBlur={handleInputBlur} required autofocus error={error['mobileNumber']} errorText={t('mobile_no_err_msg')} isPreFilledCountryCode data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />
          <TextField testId="add_edit_name" name="fullName" label={t('farmer_name')} value={fullName} onChange={handleInputChange} onBlur={handleInputBlur} required error={error['fullName']} data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />
        </div>
      </div>
      <hr />
      <div className={`mt-4 pt-4 pb-32 flex-1 flex flex-col gap-4 px-4 ${isPopup ? 'h-full overflow-y-scroll ' : ''} `}>
        <h5 className="text-lg">{t('address')}</h5>

        <TextField testId="add_edit_pin_code" name="pinCode" label={t('pin_code')} value={pinCode} onChange={handleInputChange} onBlur={handleInputBlur} required error={error['pinCode']} errorText={t('pin_code_err_msg')} data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <TextField testId="add_edit_state" name="state" label={t('state')} value={state} disabled data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <TextField testId="add_edit_district" name="district" label={t('district')} value={district} disabled data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <TextField testId="add_edit_block" name="block" label={t('block')} value={formBlock} required isDropDown dropdownTitle={t('choose_block')} disabled={pinCode.toString().length < 6 || error['pinCode']} options={blocks} onChange={handleInputChange} onDisabledClick={() => setErrorModalState({
        allowClose: true,
        show: true,
        message: t('pin_code_err_msg'),
        actionNode: <Okay handleClick={hideErrorModal} />
      })} data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <TextField testId="add_edit_village" name="village" label={t('village')} dropdownTitle={t('choose_village')} value={formVillage} isDropDown options={villages} disabled={pinCode.toString().length < 6 || error['pinCode']} onChange={handleInputChange} data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <TextField testId="add_edit_address" name="address" label={t('line_address')} value={address} onChange={handleInputChange} onBlur={handleInputBlur} error={error['address']} required data-sentry-element="TextField" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />

        <ActionButton disabled={isFormInvalid() || finalLoading} label={t('save_and_next')} onClick={e => {
        e.preventDefault();
        if (!loading.updating) {
          handleSaveAndProceed();
        }
      }} data-sentry-element="ActionButton" data-sentry-source-file="AddNewFarmerWithAddress.tsx" />
      </div>
      <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="AddNewFarmerWithAddress.tsx">
        {errorModalState.actionNode}
      </ErrorModal>
      {finalLoading ? <Spinner /> : null}
    </div>;
};
export default AddNewFarmerWithAddress;